import {
  DeploymentEnvironment,
  getEnvironment,
} from '@/utils/environmentUtils';

export type BraintrustFilterOriginType = 'form' | 'btql';

export type BraintrustMetadataField =
  | 'conversation_id'
  | 'document_id'
  | 'document_summary_id'
  | 'entity_id'
  | 'household_id'
  | 'memo_id'
  | 'presentation_id'
  | 'presentation_target_id'
  | 'temporalWorkflowID'
  | 'tile_id';

type BraintrustFilter = {
  text: string;
  label: string;
  originType: BraintrustFilterOriginType;
};

export interface BraintrustMetadataFilter {
  field: BraintrustMetadataField;
  value: string;
  originType?: BraintrustFilterOriginType;
  /**
   * Wrap the filter expression in any_span(...) so it matches traces where
   * the metadata is recorded on a child span rather than the root span.
   */
  matchAnySpan?: boolean;
}

const BRAINTRUST_PROJECT_NAME_MAP: Record<DeploymentEnvironment, string> = {
  development: 'Local',
  staging: 'Staging',
  prod: 'Production',
};

function getEncodedBraintrustExpression(
  field: BraintrustMetadataField,
  value: string
): string {
  return encodeURIComponent(`metadata.${field}%20%3D%20%22${value}%22`);
}

function toBraintrustFilter({
  field,
  value,
  originType = 'form',
  matchAnySpan = false,
}: BraintrustMetadataFilter): BraintrustFilter {
  const expression = getEncodedBraintrustExpression(field, value);
  // The any_span wrapper itself is plain text in the search param — only the
  // inner metadata expression is percent-encoded.
  const text = matchAnySpan ? `any_span(${expression})` : expression;

  if (originType === 'btql') {
    return {
      text,
      label: text,
      originType,
    };
  }

  return {
    // Braintrust expects these filter values to remain percent-encoded inside
    // the JSON `search` query param, so we encode the text and label here.
    text,
    label: encodeURIComponent(`metadata.${field}%20equals%20${value}`),
    originType,
  };
}

export function getBraintrustLogsUrl({
  filters,
}: {
  filters: BraintrustMetadataFilter[];
}): string {
  const environment = getEnvironment();
  const projectName = BRAINTRUST_PROJECT_NAME_MAP[environment];
  const encodedProjectName = encodeURIComponent(projectName);
  const search = JSON.stringify({
    filter: filters.map(toBraintrustFilter),
  });

  return `https://www.braintrust.dev/app/Luminary%20Platforms/p/${encodedProjectName}/logs?search=${search}&range=%22all%22`;
}

export function getDocumentSummaryBraintrustLogsUrl({
  householdId,
  documentId,
  documentSummaryId,
}: {
  householdId: string;
  documentId: string;
  documentSummaryId: string;
}): string {
  return getBraintrustLogsUrl({
    filters: [
      {
        field: 'household_id',
        value: householdId,
      },
      {
        field: 'document_summary_id',
        value: documentSummaryId,
      },
      {
        field: 'document_id',
        value: documentId,
        originType: 'btql',
      },
    ],
  });
}

export function getHouseholdChatBraintrustLogsUrl({
  conversationId,
}: {
  conversationId: string;
}): string {
  return getBraintrustLogsUrl({
    filters: [
      {
        field: 'conversation_id',
        value: conversationId,
        // Chat turn metadata lands on child spans of the trace, so the
        // filter has to match any span rather than just the root.
        matchAnySpan: true,
      },
    ],
  });
}

export function getFormSubmissionBraintrustLogsUrl({
  temporalWorkflowId,
}: {
  temporalWorkflowId: string;
}): string {
  return getBraintrustLogsUrl({
    filters: [
      {
        field: 'temporalWorkflowID',
        value: temporalWorkflowId,
        matchAnySpan: true,
      },
    ],
  });
}

export function getMemoBraintrustLogsUrl({
  householdId,
  memoId,
}: {
  householdId: string;
  memoId: string;
}): string {
  return getBraintrustLogsUrl({
    filters: [
      {
        field: 'household_id',
        value: householdId,
      },
      {
        field: 'memo_id',
        value: memoId,
      },
    ],
  });
}

export function getPresentationBraintrustLogsUrl({
  householdId,
  presentationId,
  presentationTargetId,
}: {
  householdId: string;
  presentationId: string;
  presentationTargetId?: string;
}): string {
  return getBraintrustLogsUrl({
    filters: [
      {
        field: 'household_id',
        value: householdId,
      },
      {
        field: 'presentation_id',
        value: presentationId,
      },
      ...(presentationTargetId
        ? [
            {
              field: 'presentation_target_id' as const,
              value: presentationTargetId,
            },
          ]
        : []),
    ],
  });
}
